import { useState, useCallback } from "react";
import { Branch } from "@/types/supabase";
import { createApiService, fetchWithAuth } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { toastHandler } from "@/lib/ToastHandler";
import { User } from "@auth0/auth0-spa-js";

export function useBranchesApi() {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken } = useAuth();

  const getTokenWithErrorHandling = useCallback(async () => {
    try {
      return await getAccessToken();
    } catch (error) {
      setError("Authentication error. Please try logging in again.");
      throw error;
    }
  }, [getAccessToken]);

  const branchesService = createApiService<Branch>(
    "/branch/branches",
    getTokenWithErrorHandling,
  );

  // Fetch paginated branches for a brand
  const fetchBranchesPaginated = async ({
    page = 1,
    limit = 10,
    brandId,
    search,
    stateFilter,
    user,
  }: {
    page?: number;
    limit?: number;
    brandId: string | number;
    search?: string;
    stateFilter?: string;
    user?: User;
  }) => {
    setLoading(true);
    try {
      // log the place where this is called
      const token = await getTokenWithErrorHandling();
      let url = `/branch/branches?page=${page}&limit=${limit}&brandId=${brandId}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      if (user && user.email) {
        url += `&email=${encodeURIComponent(user.email)}`;
      }
      if (stateFilter) {
        url += `&stateFilter=${stateFilter}`;
      }
      const response = await fetchWithAuth(url, token);
      if (!response) throw new Error("Error al obtener las sucursales");

      setBranches(response.data);
      setTotalCount(response.total);
      setTotalPages(response.totalPages);
      setCurrentPage(response.page);
      setError(null);
      return response;
    } catch (err) {
      setError("Error al obtener las sucursales");
      toastHandler({
        description: "Error al obtener las sucursales",
        variant: "destructive",
        title: "Error",
      });
    } finally {
      setLoading(false);
    }
  };

  const createBranch = async (branchData: Partial<Branch>) => {
    try {
      const token = await getAccessToken();
      const newBranch = await fetchWithAuth(`/branch/branches`, token, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(branchData),
      });
      setBranches((prev) =>
        [...prev, newBranch].sort((a, b) => a.name.localeCompare(b.name)),
      );
      setTotalCount((prev) => prev + 1);
      toastHandler({
        description: `Sucursal "${newBranch.name}" creada correctamente`,
        title: "Success",
        variant: "success",
      });
      return newBranch;
    } catch (error) {
      toastHandler({
        description:
          error instanceof Error ? error.message : "Error al crear la sucursal",
        variant: "destructive",
        title: "Error",
      });
      throw error;
    }
  };

  const updateBranch = async (id: number, branchData: Partial<any>) => {
    try {
      const updatedBranch = await branchesService.update(id, branchData);
      setBranches((prev) =>
        prev.map((branch) =>
          branch.id === id ? { ...branch, ...updatedBranch } : branch,
        ),
      );
      toastHandler({
        description: `La sucursal "${updatedBranch.name}" se actualizó correctamente`,
        title: "Éxito",
        variant: "success",
      });
      return updatedBranch;
    } catch (error) {
      toastHandler({
        description: "Failed to update branch",
        variant: "destructive",
        title: "Error",
      });
      throw error;
    }
  };

  const deleteBranch = async (id: number) => {
    try {
      await branchesService.delete(id);
      setBranches((prev) => prev.filter((branch) => branch.id !== id));
      setTotalCount((prev) => Math.max(0, prev - 1));
      toastHandler({
        description: "Branch deleted successfully",
        variant: "success",
        title: "Success",
      });
    } catch (error) {
      toastHandler({
        description: "Failed to delete branch",
        variant: "destructive",
        title: "Error",
      });
      throw error;
    }
  };

  const handleToggleBranchStatus = async (branchId: number) => {
    try {
      const branchToUpdate = branches.find((b) => b.id === branchId);
      if (!branchToUpdate) {
        toastHandler({
          title: "Error",
          description: "Sucursal no encontrada.",
          variant: "destructive",
        });
        return;
      }
      const updatedBranch = await updateBranch(branchToUpdate.id, {
        branch: { is_active: !branchToUpdate.is_active },
      });
      setBranches((prev) =>
        prev.map((b) =>
          b.id === branchId ? { ...b, is_active: updatedBranch.is_active } : b,
        ),
      );
    } catch (error: any) {
      toastHandler({
        title: "Error",
        description: "No se pudo actualizar el estado de la sucursal.",
        variant: "destructive",
      });
    }
  };

  const toggleOrderGrouping = async (branchId: number, enabled: boolean) => {
    try {
      const token = await getTokenWithErrorHandling();
      const response = await fetchWithAuth(
        `/branch/branches/${branchId}/order-grouping`,
        token,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ order_grouping_enabled: enabled }),
        },
      );

      // Update local state
      setBranches((prev) =>
        prev.map((b) =>
          b.id === branchId ? { ...b, order_grouping_enabled: enabled } : b,
        ),
      );

      toastHandler({
        title: "Éxito",
        description: `Agrupación de órdenes ${enabled ? "activada" : "desactivada"} correctamente`,
        variant: "success",
      });

      return response;
    } catch (error: any) {
      toastHandler({
        title: "Error",
        description:
          "No se pudo actualizar la configuración de agrupación de órdenes.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const hasBranches = branches.length > 0;

  return {
    branches,
    totalCount,
    totalPages,
    currentPage,
    loading,
    error,
    hasBranches,
    fetchBranchesPaginated,
    createBranch,
    updateBranch,
    deleteBranch,
    handleToggleBranchStatus,
    toggleOrderGrouping,
  };
}
